import { useEffect, useState } from 'react';
import { AlertTriangle, Check, RefreshCw } from 'lucide-react';
import Button from '../ui/Button';
import { api } from '../../api/client';
import { formatCents } from '../../utils/formatters';

/**
 * Apply-COD-Credit panel, rendered inside the EditCheckModal when the check
 * has a check_payment_mismatch finding. Fetches pending credits for the shop
 * (optionally filtered by vendor), shows the gap vs the check, and lets the
 * auditor pick a credit to apply.
 */
export default function ApplyCreditPanel({ check, onApplied }) {
  const [credits, setCredits] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selected, setSelected] = useState(null);
  const [customAmountStr, setCustomAmountStr] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const findings = check?.audit_findings || [];
  const hasMismatch = findings.includes('check_payment_mismatch');

  // Compute the current gap (invoice sum - check amount)
  const invoiceSum = (check?.invoices || []).reduce((s, i) => s + (i.amount_cents || 0), 0);
  const gapCents = invoiceSum - (check?.amount_cents || 0);
  const gapLabel = gapCents > 0
    ? `Underpaid by ${formatCents(Math.abs(gapCents))} — invoice sum is more than the check.`
    : gapCents < 0
      ? `Overpaid by ${formatCents(Math.abs(gapCents))} — check is more than invoice sum.`
      : 'Reconciled.';

  useEffect(() => {
    if (!hasMismatch || !check?.shop_id) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    api.listPendingCodCredits(check.shop_id, check.vendor_profile_id || null)
      .then(d => { if (!cancelled) { setCredits(d.credits || []); setLoading(false); } })
      .catch(e => { if (!cancelled) { setError(e.message || String(e)); setLoading(false); } });
    return () => { cancelled = true; };
  }, [hasMismatch, check?.shop_id, check?.vendor_profile_id]);

  if (!hasMismatch) return null;

  const handleApply = async () => {
    if (!selected) return;
    // Use custom amount if provided, else the gap (rounded to cents)
    const customCents = parseFloat(customAmountStr);
    const amountCents = Number.isFinite(customCents) && customCents > 0
      ? Math.round(customCents * 100)
      : Math.abs(gapCents);
    setSubmitting(true);
    try {
      const result = await api.applyCreditToCheck(check.id, selected, amountCents);
      onApplied?.(result);
    } catch (e) {
      setError(e.message || String(e));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="mt-4 rounded-lg border border-[var(--color-action-300)] bg-[var(--color-action-50)] p-4">
      <div className="flex items-start gap-2 mb-3">
        <AlertTriangle className="w-4 h-4 text-[var(--color-action-700)] shrink-0 mt-0.5" />
        <div className="flex-1 min-w-0">
          <div className="text-[13px] font-bold text-[var(--color-action-800)]">Apply COD Credit</div>
          <div className="text-[12px] text-[var(--color-action-700)] mt-0.5">{gapLabel}</div>
        </div>
      </div>

      {loading && (
        <div className="flex items-center gap-2 text-[12px] text-[var(--color-warm-600)] py-2">
          <RefreshCw className="w-3 h-3 animate-spin" />
          Loading pending credits…
        </div>
      )}

      {error && (
        <div className="text-[12px] text-[var(--color-danger-700)] py-2">Error: {error}</div>
      )}

      {!loading && !error && credits?.length === 0 && (
        <div className="text-[12px] text-[var(--color-warm-600)] italic py-2">
          No pending COD credits for this shop. If the manager mentioned a vendor credit was used,
          it may not yet be in Tekmetric or uploaded as a credit memo. Flag as mismatch for now.
        </div>
      )}

      {!loading && !error && credits && credits.length > 0 && (
        <div className="space-y-1.5">
          {credits.map(c => (
            <label
              key={c.id}
              className={`flex items-center gap-2 px-3 py-2 rounded-md border cursor-pointer text-[12px] ${
                selected === c.id
                  ? 'border-[var(--color-action-500)] bg-white'
                  : 'border-[var(--color-warm-200)] bg-white hover:border-[var(--color-warm-400)]'
              }`}
            >
              <input
                type="radio"
                name="cod-credit"
                value={c.id}
                checked={selected === c.id}
                onChange={() => setSelected(c.id)}
                className="w-3.5 h-3.5"
              />
              <span className="font-bold tabular-nums w-16 text-right">
                {formatCents(c.credit_amount_cents || 0)}
              </span>
              <span className="flex-1 min-w-0 truncate">
                {c.vendor_name}
                {c.return_order_number && <span className="text-[var(--color-warm-500)]"> · RO {c.return_order_number}</span>}
              </span>
              <span className="text-[10px] uppercase tracking-wider text-[var(--color-warm-500)]">
                {c.source === 'credit_memo' ? 'memo' : 'return'}
              </span>
            </label>
          ))}

          <div className="flex items-center gap-2 mt-2 pt-2 border-t border-[var(--color-action-300)]">
            <span className="text-[11px] text-[var(--color-warm-600)]">Amount to apply ($):</span>
            <input
              type="number"
              step="0.01"
              inputMode="decimal"
              placeholder={(Math.abs(gapCents) / 100).toFixed(2)}
              value={customAmountStr}
              onChange={e => setCustomAmountStr(e.target.value)}
              className="w-24 px-2 py-1 rounded border border-[var(--color-warm-200)] text-[12px] tabular-nums text-right"
            />
            <Button
              onClick={handleApply}
              loading={submitting}
              disabled={!selected || submitting}
              className="flex items-center gap-1.5 ml-auto"
            >
              <Check className="w-3.5 h-3.5" />
              Apply selected credit
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
